import { graphql } from '@/lib/graphql';
import { useMutation } from '@/lib/graphql/hooks';
import { useUser } from '@/lib/auth';
import { createUrlWithRedirectQuery } from '@/utils/url';
import { ConfirmDialog } from '@/components/Dialog';
import { BookmarkIcon } from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

// --- GraphQL Mutation 定義 ---
const CREATE_BOOKMARK_MUTATION = graphql(`
  mutation createBookmark($input: CreateArticleBookmarkInput!) {
    createArticleBookmark(input: $input) {
      success
      userErrors {
        message
        code
      }
      articleBookmark {
        id
      }
    }
  }
`);

const DELETE_BOOKMARK_MUTATION = graphql(`
  mutation deleteBookmark($input: DeleteArticleBookmarkInput!) {
    deleteArticleBookmark(input: $input) {
      success
      userErrors {
        message
        code
      }
    }
  }
`);
// --- ---

interface BookmarkButtonProps {
  code: string;
  title: string;
  url: string;
  isBookmarked: boolean;
  className?: string;
}

export const BookmarkButton = ({
  code,
  title,
  url,
  isBookmarked,
  className,
}: BookmarkButtonProps) => {
  const { t } = useTranslation();
  const { data: user, isLoading: isUserLoading } = useUser();
  const [isLoginRequiredDialogOpen, setIsLoginRequiredDialogOpen] =
    useState(false);
  const [createBookmark, { loading: creating }] = useMutation(
    CREATE_BOOKMARK_MUTATION
    // TODO: 手動キャッシュ更新を削除し、Apollo Clientの自動更新に任せる
    // apollo-cache-patterns.md のルールに従って修正が必要
  );
  const [deleteBookmark, { loading: deleting }] = useMutation(
    DELETE_BOOKMARK_MUTATION
    // TODO: 手動キャッシュ更新を削除し、Apollo Clientの自動更新に任せる
    // apollo-cache-patterns.md のルールに従って修正が必要
  );
  const isLoading = creating || deleting;

  const openLoginRequiredDialog = () => {
    setIsLoginRequiredDialogOpen(true);
  };

  const closeLoginRequiredDialog = () => {
    setIsLoginRequiredDialogOpen(false);
  };

  const goToLogin = () => {
    const redirectPath = `${window.location.pathname}${window.location.search}`;
    window.location.href = createUrlWithRedirectQuery(
      '/auth/login',
      redirectPath
    );
  };

  const handleGraphQLError = (error: unknown) => {
    const graphQLErrorCodes = (
      error as
        | { graphQLErrors?: { extensions?: { code?: string } }[] }
        | null
        | undefined
    )?.graphQLErrors;
    const isUnauthorized = graphQLErrorCodes?.some(
      (graphQLError) => graphQLError?.extensions?.code === 'UNAUTHENTICATED'
    );

    if (isUnauthorized) {
      openLoginRequiredDialog();
      return;
    }

    console.error('Failed to update bookmark', error);
  };

  const handleToggle = async () => {
    if (isUserLoading) return;

    if (!user) {
      openLoginRequiredDialog();
      return;
    }

    if (isBookmarked) {
      try {
        await deleteBookmark({
          variables: { input: { articleId: code } },
          update: (cache) => {
            cache.modify({
              id: cache.identify({ __typename: 'ActiveArticle', code }),
              fields: { isBookmarkedByCurrentUser: () => false },
            });
          },
        });
      } catch (error) {
        handleGraphQLError(error);
      }
    } else {
      try {
        await createBookmark({
          variables: {
            input: {
              articleId: code,
              backupArticleTitle: title,
              backupArticleUrl: url,
            },
          },
          update: (cache) => {
            cache.modify({
              id: cache.identify({ __typename: 'ActiveArticle', code }),
              fields: { isBookmarkedByCurrentUser: () => true },
            });
          },
        });
      } catch (error) {
        handleGraphQLError(error);
      }
    }
  };

  const label = isBookmarked ? t('ブックマーク済み') : t('ブックマークする');

  return (
    <>
      <button
        type="button"
        onClick={handleToggle}
        disabled={isLoading || isUserLoading}
        aria-label={label}
        title={label}
        className={clsx(
          'grid size-7 place-items-center rounded-full',
          isBookmarked ? 'bg-rw-dark-gray' : 'bg-rw-white',
          className
        )}
      >
        {isBookmarked ? (
          <BookmarkSolidIcon className="size-4 stroke-2 text-rw-white" />
        ) : (
          <BookmarkIcon className="size-4 stroke-2" />
        )}
      </button>
      <ConfirmDialog
        isOpen={isLoginRequiredDialogOpen}
        onClose={closeLoginRequiredDialog}
        onConfirm={goToLogin}
        title={t('ログインが必要です')}
        messages={[
          t('この操作はログインユーザーのみご利用いただけます。'),
          t('ログインしてブックマークを続けることができます。'),
        ]}
        confirmText={t('ログイン')}
        cancelText={t('キャンセル')}
      />
    </>
  );
};
